import {
  List,
  Datagrid,
  TextField as RATextField,
  Edit,
  Create,
  SimpleForm,
  TextInput,
  SelectInput,
  required,
  ReferenceField,
  ChipField,
  FunctionField,
  BooleanField,
  DateField,
  TopToolbar,
  CreateButton,
  ExportButton,
  FilterButton,
  useRecordContext,
  Button,
  Show,
  SimpleShowLayout,
  TabbedShowLayout,
  Tab,
  ArrayField,
  SingleFieldList,
  useRefresh,
  useNotify,
  useDataProvider,
  Loading,
  useCreate,
  useRedirect,
} from "react-admin";
import { Card, CardContent, CardHeader, Chip, Box, Typography, Grid, Alert, CircularProgress, Paper, ButtonBase } from "@mui/material";
import { useState, useEffect } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SettingsIcon from "@mui/icons-material/Settings";
import SecurityIcon from "@mui/icons-material/Security";
import { JSONSchemaForm } from "../../components/form/json.schema.form";
import { apiClient } from "../../framework/api/api.client";

/**
 * Hook to fetch available authentication providers from the API
 */
const useAuthenticationProviders = () => {
  const [providers, setProviders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        console.log('🔐 [useAuthenticationProviders] Fetching providers...');
        
        const data = await apiClient.get<any[]>('/api/authentication-providers/providers');
        
        console.log('🔐 [useAuthenticationProviders] Received providers:', data);
        
        // Transform to react-admin choice format
        const choices = data.map((provider: any) => ({
          id: provider.key,
          name: provider.name,
          description: provider.description,
          category: provider.category,
          logoUrl: provider.logoUrl,
        }));
        
        setProviders(choices);
        setLoading(false);
      } catch (err: any) {
        console.error('❌ [useAuthenticationProviders] Failed to fetch authentication providers:', err);
        setError(err.message);
        setLoading(false);
        // Fallback to hardcoded list
        setProviders([
          { id: 'msal', name: 'Microsoft (MSAL)', category: 'oidc' },
          { id: 'keycloak', name: 'Keycloak', category: 'oidc' },
        ]);
      }
    };

    fetchProviders();
  }, []);

  return { providers, loading, error };
};

/**
 * Status badge component
 */
const AuthProviderStatusField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const statusColors: Record<string, string> = {
    active: 'success',
    configured: 'info',
    draft: 'default',
    inactive: 'warning',
    error: 'error',
  };

  const statusIcons: Record<string, JSX.Element> = {
    active: <CheckCircleIcon />,
    configured: <SettingsIcon />,
    draft: <WarningIcon />,
    inactive: <PauseIcon />,
    error: <ErrorIcon />,
  };

  return (
    <Chip
      label={record.status}
      color={statusColors[record.status] as any}
      size="small"
      icon={statusIcons[record.status]}
    />
  );
};

/**
 * Provider badge component
 */
const ProviderField = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <SecurityIcon sx={{ fontSize: '1.25rem', color: 'white' }} />
      <Typography variant="body2">
        {record.providerKey}
      </Typography>
    </Box>
  );
};

/**
 * Test provider button
 */
const TestAuthProviderButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [testing, setTesting] = useState(false);

  const handleTest = async () => {
    setTesting(true);
    try {
      console.log(`🔐 [TestAuthProviderButton] Testing auth provider: ${record.id}`);
      
      const result = await apiClient.post(`/api/authentication-providers/${record.id}/test`, {});
      
      if (result.success) {
        notify(`Authentication provider test successful! (${result.latency}ms)`, { type: 'success' });
      } else {
        notify(`Authentication provider test failed: ${result.message}`, { type: 'error' });
      }
    } catch (error: any) {
      console.error(`❌ [TestAuthProviderButton] Test failed:`, error);
      notify(`Authentication provider test failed: ${error.message}`, { type: 'error' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Button
      label="Test"
      onClick={handleTest}
      disabled={testing}
      startIcon={testing ? <CircularProgress size={14} /> : <PlayArrowIcon />}
    />
  );
};

/**
 * Activate/Deactivate button
 */
const ActivateAuthProviderButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);

  const isActive = record?.status === 'active';

  const handleToggle = async () => {
    setLoading(true);
    try {
      const endpoint = isActive ? 'deactivate' : 'activate';
      console.log(`🔐 [ActivateAuthProviderButton] ${endpoint} auth provider: ${record.id}`);
      
      await apiClient.post(`/api/authentication-providers/${record.id}/${endpoint}`, {});
      
      notify(`Authentication provider ${isActive ? 'deactivated' : 'activated'} successfully`, { type: 'success' });
      refresh();
    } catch (error: any) {
      console.error(`❌ [ActivateAuthProviderButton] Failed to ${isActive ? 'deactivate' : 'activate'}:`, error);
      notify(`Failed to ${isActive ? 'deactivate' : 'activate'} authentication provider: ${error.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      label={isActive ? 'Deactivate' : 'Activate'}
      onClick={handleToggle}
      disabled={loading}
      color={isActive ? 'warning' : 'primary'}
      startIcon={loading ? <CircularProgress size={14} /> : (isActive ? <PauseIcon /> : <PlayArrowIcon />)}
    />
  );
};

/**
 * List actions
 */
const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <CreateButton />
    <ExportButton />
  </TopToolbar>
);

/**
 * Authentication Provider List
 */
const AuthenticationProviderListContent = ({ providers }: { providers: any[] }) => {
  return (
    <List
      actions={<ListActions />}
      filters={[
        <TextInput key="search" label="Search" source="q" alwaysOn />,
        <SelectInput
          key="status"
          label="Status"
          source="status"
          choices={[
            { id: 'active', name: 'Active' },
            { id: 'configured', name: 'Configured' },
            { id: 'draft', name: 'Draft' },
            { id: 'inactive', name: 'Inactive' },
            { id: 'error', name: 'Error' },
          ]}
        />,
        <SelectInput
          key="providerKey"
          label="Provider"
          source="providerKey"
          choices={providers}
          optionText="name"
          optionValue="id"
        />,
      ]}
    >
      <Datagrid rowClick="edit">
        <RATextField source="name" />
        <RATextField source="key" />
        <FunctionField label="Provider" render={() => <ProviderField />} />
        <FunctionField label="Status" render={() => <AuthProviderStatusField />} />
        <RATextField source="providerType" label="Type" />
        <BooleanField source="isDefault" label="Default" />
        <FunctionField
          label="Environment"
          render={(record: any) => record.metadata?.environment || 'N/A'}
        />
        <DateField source="lastUsedAt" label="Last Used" showTime />
      </Datagrid>
    </List>
  );
};

export const AuthenticationProviderList = () => {
  const { providers, loading } = useAuthenticationProviders();

  if (loading) {
    return <Loading />;
  }

  return <AuthenticationProviderListContent providers={providers} />;
};

/**
 * Authentication Provider Show (Detail View)
 */
export const AuthenticationProviderShow = () => {
  return (
    <Show>
      <TabbedShowLayout>
        <Tab label="Overview">
          <RATextField source="name" />
          <RATextField source="key" />
          <RATextField source="description" />
          <RATextField source="providerKey" label="Provider" />
          <FunctionField label="Status" render={() => <AuthProviderStatusField />} />
          <RATextField source="providerType" label="Type" />
          <BooleanField source="isDefault" label="Default Provider" />
          <DateField source="createdAt" showTime />
          <DateField source="updatedAt" showTime />
          <DateField source="lastUsedAt" showTime />
        </Tab>

        <Tab label="Configuration">
          <FunctionField
            label="Configuration"
            render={(record: any) => (
              <pre style={{
                background: '#0f172a',
                color: '#e2e8f0',
                padding: '16px',
                borderRadius: '8px',
                overflow: 'auto',
                border: '1px solid #1f2937',
              }}>
                {JSON.stringify(record.config, null, 2)}
              </pre>
            )}
          />
        </Tab>

        <Tab label="Health">
          <FunctionField
            label="Last Check"
            render={(record: any) => (
              <Box>
                {record.health?.lastCheckAt ? (
                  <>
                    <Typography variant="body2">
                      <strong>Last Check:</strong> {new Date(record.health.lastCheckAt).toLocaleString()}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Status:</strong>{' '}
                      <Chip
                        label={record.health.isHealthy ? 'Healthy' : 'Unhealthy'}
                        color={record.health.isHealthy ? 'success' : 'error'}
                        size="small"
                      />
                    </Typography>
                    {record.health.lastError && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {record.health.lastError}
                      </Alert>
                    )}
                  </>
                ) : (
                  <Alert severity="info">No health checks performed yet</Alert>
                )}
              </Box>
            )}
          />
        </Tab>

        <Tab label="Metadata">
          <FunctionField
            label="Metadata"
            render={(record: any) => (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Environment</Typography>
                  <Typography variant="body2">
                    {record.metadata?.environment || 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Owner</Typography>
                  <Typography variant="body2">
                    {record.metadata?.owner || 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Tags</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    {record.metadata?.tags?.map((tag: string, index: number) => (
                      <Chip key={index} label={tag} size="small" />
                    )) || <Typography variant="body2">No tags</Typography>}
                  </Box>
                </Grid>
              </Grid>
            )}
          />
        </Tab>

        <Tab label="Actions">
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TestAuthProviderButton />
            <ActivateAuthProviderButton />
          </Box>
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

/**
 * Authentication Provider Edit
 */
export const AuthenticationProviderEdit = () => {
  const { providers, loading } = useAuthenticationProviders();

  if (loading) {
    return <Loading />;
  }

  return (
    <Edit>
      <AuthenticationProviderEditForm providers={providers} />
    </Edit>
  );
};

const AuthenticationProviderEditForm = ({ providers }: { providers: any[] }) => {
  const record = useRecordContext() as any;
  const [providerSchema, setProviderSchema] = useState<any>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();

  // Initialize form data from record
  useEffect(() => {
    if (record) {
      setFormData({
        name: record.name || '',
        key: record.key || '',
        description: record.description || '',
        providerType: record.providerType || 'oidc',
        ...(record.config || {}),
        metadata: record.metadata || {},
        providerKey: record.providerKey,
        status: record.status,
        isDefault: record.isDefault || false,
      });
    }
  }, [record]);

  // Fetch provider schema for editing
  useEffect(() => {
    if (!record?.providerKey) return;
    const fetchSchema = async () => {
      setSchemaLoading(true);
      try {
        const providerSchemaData = await apiClient.get(`/api/authentication-providers/providers/${record.providerKey}/schema`);

        const mergedSchema = {
          schema: {
            type: 'object',
            title: `Edit ${record?.providerKey} Authentication Provider`,
            required: ['name', 'key'],
            properties: {
              name: { type: 'string', title: 'Provider Name' },
              key: { type: 'string', title: 'Provider Key', pattern: '^[a-z0-9-_]+$' },
              description: { type: 'string', title: 'Description' },
              ...providerSchemaData.schema.properties,
              isDefault: {
                type: 'boolean',
                title: 'Set as Default Provider',
                description: 'Make this the default authentication provider for the environment',
              },
            },
          },
          uiSchema: {
            description: { 'ui:widget': 'textarea', 'ui:options': { rows: 3 } },
            ...providerSchemaData.uiSchema,
          },
        };

        setProviderSchema(mergedSchema);
      } catch (e: any) {
        notify(`Failed to load provider schema: ${e.message}`, { type: 'error' });
      } finally {
        setSchemaLoading(false);
      }
    };
    fetchSchema();
  }, [record?.providerKey, notify]);

  const handleSave = async () => {
    if (!record) return;
    try {
      const { name, key, description, providerType, providerKey, status, metadata, isDefault, ...config } = formData;
      const payload: any = {
        name,
        key,
        description,
        providerType: providerType || record.providerType,
        providerKey: providerKey || record.providerKey,
        config,
        status: status || record.status,
        metadata: metadata || {},
        isDefault: isDefault || false,
      };

      await dataProvider.update('authentication-provider', { id: record.id, data: payload, previousData: record });
      notify('Authentication provider updated successfully', { type: 'success' });
      redirect('/authentication-provider');
    } catch (e: any) {
      notify(`Failed to update authentication provider: ${e.message}`, { type: 'error' });
    }
  };

  if (!record) return <Loading />;

  return (
    <Card>
      <CardContent>
        {schemaLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : providerSchema ? (
          <>
            <Paper elevation={0} sx={{ p: 3, backgroundColor: 'background.default', mb: 3 }}>
              <JSONSchemaForm
                schema={providerSchema.schema}
                uiSchema={providerSchema.uiSchema}
                formData={formData}
                onChange={(data: any) => setFormData(data)}
              />
            </Paper>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button label="Cancel" onClick={() => redirect('/authentication-provider')} />
              <Button label="Save" onClick={handleSave} variant="contained" />
            </Box>
          </>
        ) : (
          <Alert severity="warning">Unable to load configuration form for this provider.</Alert>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Provider Logo Button
 */
const ProviderLogoButton = ({ provider, selected, onClick }: any) => {
  return (
    <ButtonBase
      onClick={() => onClick(provider)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        width: 240,
        height: 180,
        padding: 2.5,
        pt: 3,
        borderRadius: 2,
        border: selected ? '3px solid' : '2px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        backgroundColor: '#ffffff',
        color: '#000',
        boxShadow: selected ? 3 : 1,
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: 'primary.main',
          transform: 'translateY(-2px)',
          boxShadow: 3,
        },
      }}
    >
      <Box sx={{ mt: 1, mb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {provider.logoUrl && typeof provider.logoUrl === 'string' && provider.logoUrl.startsWith('http') ? (
          <img
            src={provider.logoUrl}
            alt={provider.name}
            style={{ width: 160, height: 100, objectFit: 'contain', display: 'block' }}
          />
        ) : (
          <SecurityIcon sx={{ fontSize: '5.5rem', color: '#9e9e9e' }} />
        )}
      </Box>
      <Typography variant="body2" fontWeight={selected ? 700 : 500} sx={{ color: '#000' }}>
        {provider.name}
      </Typography>
      {provider.category && (
        <Chip label={provider.category} size="small" sx={{ mt: 0.5 }} />
      )}
    </ButtonBase>
  );
};

/**
 * Authentication Provider Create
 */
export const AuthenticationProviderCreate = () => {
  const { providers, loading: providersLoading } = useAuthenticationProviders();
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [providerSchema, setProviderSchema] = useState<any>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [fullFormData, setFullFormData] = useState<any>({
    name: '',
    key: '',
    description: '',
    providerType: 'oidc',
    isDefault: false,
    metadata: {
      environment: 'development',
    },
    config: {},
  });

  const [create] = useCreate();
  const redirect = useRedirect();
  const notify = useNotify();

  // Fetch provider schema and merge with base schema when provider is selected
  useEffect(() => {
    if (selectedProvider) {
      const fetchSchema = async () => {
        setSchemaLoading(true);
        try {
          console.log(`🔐 [AuthenticationProviderCreate] Fetching schema for provider: ${selectedProvider.id}`);
          
          const providerSchemaData = await apiClient.get(`/api/authentication-providers/providers/${selectedProvider.id}/schema`);
          
          console.log(`🔐 [AuthenticationProviderCreate] Received provider schema:`, providerSchemaData);
          
          // Merge base auth provider schema with provider-specific schema
          const mergedSchema = {
            schema: {
              type: 'object',
              title: `Create ${selectedProvider.name} Authentication Provider`,
              required: ['name', 'key'],
              properties: {
                name: {
                  type: 'string',
                  title: 'Provider Name',
                  description: 'A friendly name for this authentication provider',
                },
                key: {
                  type: 'string',
                  title: 'Provider Key',
                  description: 'Unique identifier for this provider (lowercase, no spaces)',
                  pattern: '^[a-z0-9-_]+$',
                },
                description: {
                  type: 'string',
                  title: 'Description',
                  description: 'Optional description of this authentication provider',
                },
                ...providerSchemaData.schema.properties,
                isDefault: {
                  type: 'boolean',
                  title: 'Set as Default Provider',
                  description: 'Make this the default authentication provider for the environment',
                  default: false,
                },
              },
            },
            uiSchema: {
              name: {
                'ui:autofocus': true,
              },
              key: {
                'ui:help': 'Use lowercase letters, numbers, hyphens, and underscores only',
              },
              description: {
                'ui:widget': 'textarea',
                'ui:options': {
                  rows: 3,
                },
              },
              ...providerSchemaData.uiSchema,
            },
          };
          
          setProviderSchema(mergedSchema);
          setSchemaLoading(false);
        } catch (err: any) {
          console.error(`❌ [AuthenticationProviderCreate] Failed to load provider schema:`, err);
          notify(`Failed to load provider schema: ${err.message}`, { type: 'error' });
          setSchemaLoading(false);
        }
      };
      
      fetchSchema();
    }
  }, [selectedProvider, notify]);

  const handleProviderSelect = (provider: any) => {
    setSelectedProvider(provider);
    setFullFormData({
      ...fullFormData,
      providerKey: provider.id,
    });
  };

  const handleSubmit = async () => {
    const { name, key, description, providerType, isDefault, ...config } = fullFormData;
    
    const payload = {
      name,
      key,
      description,
      providerType: providerType || 'oidc',
      providerKey: selectedProvider.id,
      config,
      isDefault: isDefault || false,
      status: 'draft',
      metadata: {
        environment: 'development',
      },
    };

    try {
      await create('authentication-provider', { data: payload });
      notify('Authentication provider created successfully', { type: 'success' });
      redirect('/authentication-provider');
    } catch (error: any) {
      notify(`Failed to create authentication provider: ${error.message}`, { type: 'error' });
    }
  };

  if (providersLoading) {
    return <Loading />;
  }

  return (
    <Create>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Create New Authentication Provider
          </Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            Select a provider below to configure authentication for your application. Each provider has its own configuration requirements.
          </Alert>

          {/* Provider Selection */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            1. Select Provider
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {providers.map((provider: any) => (
              <Grid item key={provider.id}>
                <ProviderLogoButton
                  provider={provider}
                  selected={selectedProvider?.id === provider.id}
                  onClick={handleProviderSelect}
                />
              </Grid>
            ))}
          </Grid>

          {selectedProvider && (
            <>
              {/* Unified Form using JSON Schema */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                2. Configure Authentication Provider
              </Typography>
              {schemaLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : providerSchema ? (
                <>
                  <Paper elevation={0} sx={{ p: 3, backgroundColor: 'background.default', mb: 3 }}>
                    <JSONSchemaForm
                      schema={providerSchema.schema}
                      uiSchema={providerSchema.uiSchema}
                      formData={fullFormData}
                      onChange={(data: any) => setFullFormData(data)}
                    />
                  </Paper>

                  {/* Submit Button */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button label="Cancel" onClick={() => redirect('/authentication-provider')} />
                    <Button
                      label="Create Authentication Provider"
                      onClick={handleSubmit}
                      variant="contained"
                      disabled={!fullFormData.name || !fullFormData.key}
                    />
                  </Box>
                </>
              ) : (
                <Alert severity="warning">
                  Unable to load configuration form for this provider.
                </Alert>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Create>
  );
};

